import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  MapPin,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison + 5 engagements (liste) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison directe
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons de déménagement à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Recevez rapidement vos cartons de déménagement et fournitures
            d&apos;emballage directement à {city.name}. Nous vous proposons une
            solution pratique pour obtenir tout le matériel nécessaire à la
            préparation de votre déménagement, sans avoir à vous déplacer.
          </p>
          <p className="mt-2 text-[13px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
            Nos engagements
          </p>
        </div>

        <ul className="mt-6 space-y-3 max-w-3xl">
          {[
            "Livraison rapide à Clichy et dans les communes voisines.",
            "Cartons robustes et fournitures de qualité professionnelle.",
            "Commandes préparées avec soin avant expédition.",
            "Large choix de cartons, protections et accessoires d'emballage.",
            "Service client disponible pour vous accompagner dans votre projet.",
          ].map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-3 bg-white rounded-2xl border border-[#cfdbe6] p-4 md:p-5 text-[14.5px] text-[#3a4856] leading-relaxed"
            >
              <span className="flex items-center justify-center h-8 w-8 rounded-full bg-[#237cb5] text-white shrink-0">
                <CircleCheck className="h-4 w-4" strokeWidth={2.6} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-clichy-cta-shop-livraison"
          >
            Commander mes cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons reconditionnés -40% ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
            <Tag className="h-3.5 w-3.5" strokeWidth={2.6} />
            Offre éco — jusqu&apos;à -40%
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Cartons de déménagement reconditionnés à -40%
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pourquoi surpayer du matériel éphémère&nbsp;? Nous vous proposons
            une opportunité concrète de réduire vos coûts grâce à nos cartons
            de seconde main haut de gamme.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "État proche du neuf",
              body: "Ces structures n'ont servi qu'une seule fois. Triées manuellement et inspectées avec rigueur par nos soins, elles conservent l'intégralité de leurs propriétés de rigidité et de protection.",
              Icon: ShieldCheck,
            },
            {
              title: "Économie immédiate",
              body: "Profitez d'un tarif réduit de 40% par rapport au prix du neuf, une option idéale pour équiper tout votre logement sans faire exploser votre budget de préparation.",
              Icon: Tag,
            },
            {
              title: "Démarche éco-responsable",
              body: "En réutilisant ces emballages, vous participez activement à une économie circulaire qui limite le gaspillage des ressources.",
              Icon: Recycle,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-reconditionnes"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#FF7A00] hover:bg-[#E66E00] text-white font-bold text-[14px] transition-colors"
            data-testid="city-clichy-cta-reconditionne"
          >
            <Recycle className="h-4 w-4" strokeWidth={2.6} />
            Voir les cartons reconditionnés
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-clichy-cta-shop-neuf"
          >
            Voir les cartons neufs
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Pourquoi nous accorder votre confiance ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Nos engagements
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Pourquoi nous accorder votre confiance pour votre projet&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous ne nous contentons pas de vendre des boîtes en carton, nous
            sécurisons vos préparatifs à travers des engagements fermes.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Épaisseur et structure renforcées",
              body: "Contrairement aux modèles standards vendus dans le commerce, nos produits disposent d'un grammage industriel conçu pour supporter le gerbage (l'empilement) dans le camion sans s'écraser.",
              Icon: Layers,
            },
            {
              title: "Disponibilité totale",
              body: "Nos stocks sont gérés en temps réel tout au long de l'année, vous évitant les mauvaises surprises ou les retards de livraison en pleine saison de déménagement.",
              Icon: Truck,
            },
            {
              title: "Un service de proximité transparent",
              body: "Vous traitez avec un interlocuteur unique et engagé, dédié à la réussite de vos préparatifs logistiques.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-clichy-cta-shop"
          >
            Voir tous les cartons
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-clichy-cta-kits"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Choisir un kit
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-clichy-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
